import axios, { type AxiosRequestConfig } from 'axios'
import humps from 'humps'

// All requests go to /api or /auth — Vite proxies them to the backend in dev,
// CloudFront routes them in prod. No base URL needed.
const client = axios.create({
  withCredentials: true,   // send JWT cookies (on_call_session + on_call_refresh) on every request
  headers: {
    'Content-Type': 'application/json',
  },
})

// ─── Case conversion interceptors ─────────────────────────────────────────────
// The backend speaks snake_case (Jackson @JsonNaming on every DTO).
// The frontend TypeScript code uses camelCase throughout.
// These interceptors transparently translate between the two conventions.

client.interceptors.request.use((config) => {
  if (config.data && typeof config.data === 'object') {
    config.data = humps.decamelizeKeys(config.data)
  }
  return config
})

client.interceptors.response.use((response) => {
  // Skip camelization for binary responses (Blob) — humps would corrupt them
  if (response.data && typeof response.data === 'object' && !(response.data instanceof Blob)) {
    response.data = humps.camelizeKeys(response.data)
  }
  return response
})

// ─── Token refresh state ──────────────────────────────────────────────────────
// Guards against concurrent refresh calls when multiple requests fail with 401 at the same time.
// Only one refresh request is allowed in-flight; subsequent 401s wait for it, then retry.

let isRefreshing = false
let refreshQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = []

function enqueueRefresh(): Promise<void> {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject })
  })
}

function resolveQueue(): void {
  refreshQueue.forEach(({ resolve }) => resolve())
  refreshQueue = []
}

function rejectQueue(err: unknown): void {
  refreshQueue.forEach(({ reject }) => reject(err))
  refreshQueue = []
}

// ─── Response interceptor: transparent token refresh on 401 ──────────────────
//
// Flow on 401:
//   1. Call POST /auth/refresh (backend reads on_call_refresh cookie scoped to that path)
//   2. If refresh succeeds → backend sets new on_call_session + on_call_refresh cookies
//      → retry the original request → transparent to the caller
//   3. If refresh fails (refresh token absent / expired) → redirect to /login
//
// Guards:
//   - Skip if the failing request IS the refresh endpoint (avoids infinite loop)
//   - Skip if already on /login (avoids recursive redirects)
//   - Concurrent 401s share one refresh call via the queue above

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as AxiosRequestConfig & { _retried?: boolean }
    const isRefreshEndpoint = config?.url?.includes('/auth/refresh')
    const alreadyOnLogin = window.location.pathname === '/login'

    // Terminal 401: account disabled, user deleted, or role changed.
    // Cookies are already cleared by the backend — skip refresh and go straight to login.
    if (error.response?.headers?.['x-logout-required'] === 'true') {
      if (!alreadyOnLogin) window.location.href = '/login'
      return Promise.reject(error)
    }

    if (
      error.response?.status === 401 &&
      !isRefreshEndpoint &&
      !config?._retried &&
      !alreadyOnLogin
    ) {
      if (isRefreshing) {
        // Another refresh is already in-flight — wait for it, then retry
        try {
          await enqueueRefresh()
          config._retried = true
          return client.request(config)
        } catch {
          return Promise.reject(error)
        }
      }

      config._retried = true
      isRefreshing = true

      try {
        // POST /auth/refresh — browser sends on_call_refresh cookie automatically
        // because that cookie is scoped to path=/auth/refresh
        await client.post('/auth/refresh')
        isRefreshing = false
        resolveQueue()
        // Retry the original request — browser now has fresh on_call_session cookie
        return client.request(config)
      } catch (refreshError) {
        isRefreshing = false
        rejectQueue(refreshError)
        // Refresh token expired or absent — send user to login
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    // 409 Conflict — only broadcast the global "please retry" snackbar for optimistic-lock
    // conflicts (code: OPTIMISTIC_LOCK). Domain conflicts (code: DOMAIN_CONFLICT, e.g. duplicate
    // phone number) are handled inline by each mutation's onError.
    if (error.response?.status === 409 && error.response?.data?.code === 'OPTIMISTIC_LOCK') {
      window.dispatchEvent(new CustomEvent('api:conflict'))
    }

    return Promise.reject(error)
  }
)

export default client
